import React, { FunctionComponent } from "react";
import "./PepAnswers.scss";
import { PepRequest } from "../model/PepModel";
import { getRoleAlts, getMilitaryAlts } from "../pages/RolePage/RolePage";
import { PepType } from "../pages/PepContext";
import { useIntl, defineMessages, IntlShape } from "react-intl";
import { messages as isPepMessages } from "../pages/IsPep/IsPepMessages";
import {
  relationMessages,
  businessRelationMessages,
} from "../pages/RelationPage/RelationMessages";

interface Props {
  answer: PepRequest;
}

const messages = defineMessages({
  whoIsPepLabel: {
    id: "pep.answers.who.label",
  },
  relationLabel: {
    id: "pep.answers.relationship.label",
  },
  businessRelationLabel: {
    id: "pep.answers.businessRelationship.label",
  },
  nameOfPepLabel: {
    id: "pep.answers.nameOfPep.label",
  },
  birthdayOfPEPLabel: {
    id: "pep.answers.birthdayOfPEP.label",
  },
  statusLabel: {
    id: "pep.answers.status.label",
  },
  statusEndedValue: {
    id: "pep.answers.statusEnded.value",
  },
  statusOngoingValue: {
    id: "pep.answers.statusOngoing.value",
  },
  endedLabel: {
    id: "pep.answers.endedStatus.label",
  },
  roleLabel: {
    id: "pep.answers.role.label",
  },
  countryLabel: {
    id: "pep.answers.country.label",
  },
  ambassadorCountryLabel: {
    id: "pep.answers.ambassadorCountry.label",
  },
  governmentCompanyNameLabel: {
    id: "pep.answers.governmentCompanyName.label",
  },
  governmentCompanyRoleLabel: {
    id: "pep.answers.governmentCompanyRole.label",
  },
  internationalOrganisationNameLabel: {
    id: "pep.answers.companyName.label",
  },
  internationalOrganisationRoleLabel: {
    id: "pep.answers.companyRole.label",
  },
  militaryGradeLabel: {
    id: "pep.answers.militaryRank.label",
  },
});

interface PepAnswerText {
  label: string;
  value: string;
}

export function getPepAnswersTexts(
  answer: PepRequest,
  intl: IntlShape
): string | PepAnswerText[] {
  if (!answer.type || answer.type === PepType.NOT_PEP) {
    return intl.formatMessage({ id: "pep.answers.notpep" });
  }

  const entityType = answer.legalEntityType;
  const role = getRoleAlts(intl).find((alt) => alt.value === answer.role);
  const militaryRank = getMilitaryAlts(intl).find(
    (alt) => alt.value === answer.militaryRank
  );

  const result = [];

  if (answer.type) {
    result.push({
      label: intl.formatMessage(messages.whoIsPepLabel),
      value: intl.formatMessage(isPepMessages[answer.type], {
        entityType,
        name: answer.ownerName,
      }),
    });
  }

  if (answer.relationToPEP) {
    result.push({
      label: intl.formatMessage(messages.relationLabel),
      value: intl.formatMessage(relationMessages[answer.relationToPEP], {
        entityType,
        name: answer.ownerName,
      }),
    });
  }

  if (answer.businessRelation) {
    result.push({
      label: intl.formatMessage(messages.businessRelationLabel),
      value: intl.formatMessage(
        businessRelationMessages[answer.businessRelation],
        { entityType, name: answer.ownerName }
      ),
    });
  }

  if (answer.nameOfPEP) {
    result.push({
      label: intl.formatMessage(messages.nameOfPepLabel),
      value: answer.nameOfPEP,
    });
  }

  if (answer.birthdayOfPEP) {
    result.push({
      label: intl.formatMessage(messages.birthdayOfPEPLabel),
      value: answer.birthdayOfPEP,
    });
  }

  if (answer.ended) {
    result.push(
      {
        label: intl.formatMessage(messages.statusLabel),
        value: intl.formatMessage(messages.statusEndedValue),
      },
      {
        label: intl.formatMessage(messages.endedLabel),
        value: answer.ended,
      }
    );
  } else {
    result.push({
      label: intl.formatMessage(messages.statusLabel),
      value: intl.formatMessage(messages.statusOngoingValue),
    });
  }

  if (answer.country) {
    result.push({
      label: intl.formatMessage(messages.countryLabel),
      value: intl.formatDisplayName(answer.country, { type: "region" }) ?? "-",
    });
  }

  if (role) {
    result.push({
      label: intl.formatMessage(messages.roleLabel),
      value: role.text,
    });
  }

  if (answer.ambassadorCountry) {
    result.push({
      label: intl.formatMessage(messages.ambassadorCountryLabel),
      value:
        intl.formatDisplayName(answer.ambassadorCountry, {
          type: "region",
        }) ?? "-",
    });
  }

  if (answer.governmentCompany) {
    result.push({
      label: intl.formatMessage(messages.governmentCompanyNameLabel),
      value: answer.governmentCompany,
    });
  }

  if (answer.governmentCompanyRole) {
    result.push({
      label: intl.formatMessage(messages.governmentCompanyRoleLabel),
      value: answer.governmentCompanyRole,
    });
  }

  if (answer.internationalOrganisation) {
    result.push({
      label: intl.formatMessage(messages.internationalOrganisationNameLabel),
      value: answer.internationalOrganisation,
    });
  }

  if (answer.internationalOrganisationRole) {
    result.push({
      label: intl.formatMessage(messages.internationalOrganisationRoleLabel),
      value: answer.internationalOrganisationRole,
    });
  }

  if (militaryRank) {
    result.push({
      label: intl.formatMessage(messages.militaryGradeLabel),
      value: militaryRank.text,
    });
  }

  return result;
}

export const PepAnswers: FunctionComponent<Props> = ({ answer }) => {
  const intl = useIntl();
  const texts = getPepAnswersTexts(answer, intl);

  return (
    <div>
      {typeof texts === "string" ? (
        <p>{texts}</p>
      ) : (
        texts.map((item, index) => (
          <StaticInput key={index} label={item.label} value={item.value} />
        ))
      )}
    </div>
  );
};

interface StaticInputProps {
  label: string;
  value: string | React.ReactNode;
}

const StaticInput: FunctionComponent<StaticInputProps> = ({ label, value }) => {
  return (
    <div className="static-input">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
};
